import React, { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LoopIcon from '@mui/icons-material/Loop';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

// ─── Shared Primitives ────────────────────────────────────────────────────────

const nodeBase: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '10px 14px',
  fontSize: 12,
  lineHeight: 1.4,
  transition: 'border-color 120ms ease, box-shadow 120ms ease',
};

const Node: React.FC<{
  label: string;
  sub?: string;
  color?: string;
  accent?: boolean;
  dashed?: boolean;
  mono?: boolean;
  style?: React.CSSProperties;
  width?: number;
}> = ({ label, sub, color, accent, dashed, mono, style, width }) => (
  <div
    style={{
      ...nodeBase,
      borderLeft: color ? `3px solid ${color}` : undefined,
      borderStyle: dashed ? 'dashed' : 'solid',
      borderColor: dashed ? 'var(--border-hover)' : accent ? color : 'var(--border)',
      boxShadow: accent ? `0 0 0 1px ${color}30` : undefined,
      width: width ?? 'auto',
      minWidth: width ?? 'auto',
      textAlign: 'center' as const,
      ...style,
    }}
  >
    <div
      style={{
        fontWeight: 600,
        fontSize: 12,
        color: color ?? 'var(--text-primary)',
        fontFamily: mono ? 'var(--font-mono)' : undefined,
      }}
    >
      {label}
    </div>
    {sub && (
      <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>
        {sub}
      </div>
    )}
  </div>
);

const ArrowDown: React.FC<{ height?: number; label?: string; color?: string }> = ({
  height = 28,
  label,
  color = 'var(--border-hover)',
}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height,
      position: 'relative',
    }}
  >
    <div
      style={{
        width: 1,
        flex: 1,
        background: color,
      }}
    />
    <div
      style={{
        width: 0,
        height: 0,
        borderLeft: '4px solid transparent',
        borderRight: '4px solid transparent',
        borderTop: `6px solid ${color}`,
      }}
    />
    {label && (
      <span
        style={{
          position: 'absolute',
          left: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: 9,
          color: 'var(--text-tertiary)',
          fontFamily: 'var(--font-mono)',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    )}
  </div>
);

const ArrowRight: React.FC<{ width?: number; label?: string; color?: string }> = ({
  width = 36,
  label,
  color = 'var(--border-hover)',
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      width,
      position: 'relative',
      flexShrink: 0,
    }}
  >
    <div style={{ height: 1, flex: 1, background: color }} />
    <div
      style={{
        width: 0,
        height: 0,
        borderTop: '4px solid transparent',
        borderBottom: '4px solid transparent',
        borderLeft: `6px solid ${color}`,
      }}
    />
    {label && (
      <span
        style={{
          position: 'absolute',
          top: -14,
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: 9,
          color: 'var(--text-tertiary)',
          fontFamily: 'var(--font-mono)',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    )}
  </div>
);

const Badge: React.FC<{ label: string; color: string }> = ({ label, color }) => (
  <span
    style={{
      fontFamily: 'var(--font-mono)',
      fontSize: 10,
      fontWeight: 700,
      color,
      background: `${color}15`,
      border: `1px solid ${color}30`,
      padding: '2px 8px',
      borderRadius: 4,
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </span>
);

const SpecRef: React.FC<{ section: string }> = ({ section }) => (
  <span
    style={{
      fontFamily: 'var(--font-mono)',
      fontSize: 10,
      color: 'var(--text-tertiary)',
      background: 'var(--elevated)',
      padding: '1px 6px',
      borderRadius: 3,
    }}
  >
    {section}
  </span>
);

// ─── Task Type Colors ─────────────────────────────────────────────────────────

const COLORS = {
  findFirst: '#C2703E',
  assign: '#C2703E',
  curator: '#C2703E',
  pending: '#A85D33',
  designReview: '#C2703E',
  qa: '#C2703E',
  // Lifecycle
  queued: 'var(--accent)',
  claimed: 'var(--blue)',
  submitted: 'var(--positive)',
  skipped: 'var(--warning)',
  rejected: 'var(--danger)',
  superseded: 'var(--text-tertiary)',
  // Infra
  redis: '#DC382D',
  mysql: '#00758F',
  mongo: '#4DB33D',
  cron: 'var(--purple)',
  ray: '#FF6B35',
};

// ─── Section Wrapper ──────────────────────────────────────────────────────────

interface SectionProps {
  id: string;
  number: number;
  title: string;
  icon: React.ReactNode;
  specRef: string;
  description: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({
  number,
  title,
  icon,
  specRef,
  description,
  expanded,
  onToggle,
  children,
}) => (
  <div
    style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 6,
      marginBottom: 12,
      overflow: 'hidden',
    }}
  >
    <button
      onClick={onToggle}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 16px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        color: 'var(--text-primary)',
      }}
    >
      <span style={{ color: 'var(--text-tertiary)', display: 'flex' }}>{icon}</span>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          fontWeight: 700,
          color: 'var(--accent)',
          minWidth: 28,
        }}
      >
        {String(number).padStart(2, '0')}
      </span>
      <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{title}</span>
      <SpecRef section={specRef} />
      {expanded ? (
        <ExpandLessIcon style={{ fontSize: 18, color: 'var(--text-tertiary)' }} />
      ) : (
        <ExpandMoreIcon style={{ fontSize: 18, color: 'var(--text-tertiary)' }} />
      )}
    </button>
    {expanded && (
      <div style={{ borderTop: '1px solid var(--border)' }}>
        <div
          style={{
            padding: '10px 16px 10px 56px',
            fontSize: 12,
            color: 'var(--text-secondary)',
            lineHeight: 1.5,
            borderBottom: '1px solid var(--divider)',
          }}
        >
          {description}
        </div>
        <div style={{ padding: '20px 24px 24px' }}>{children}</div>
      </div>
    )}
  </div>
);

// ─── 1. Task Lifecycle ────────────────────────────────────────────────────────

const TaskLifecycleDiagram: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
    {/* queued */}
    <Node label="QUEUED" sub="Task created by regeneration service" color={COLORS.queued} accent width={220} />
    <ArrowDown label="LPOP from Redis" />

    {/* claimed */}
    <Node label="CLAIMED" sub="Labeler working — 15 min lease" color={COLORS.claimed} accent width={220} />

    {/* Branch out to 4 terminal states */}
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0, marginTop: 0 }}>
      {/* Left branches */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <ArrowDown height={24} color={COLORS.rejected} />
        <Node label="REJECTED" sub="Precondition failed" color={COLORS.rejected} width={140} />
      </div>
      <div style={{ width: 16 }} />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <ArrowDown height={24} color={COLORS.skipped} />
        <Node label="SKIPPED" sub="Labeler skipped" color={COLORS.skipped} width={140} />
      </div>
      <div style={{ width: 16 }} />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <ArrowDown height={24} color={COLORS.submitted} />
        <Node label="SUBMITTED" sub="Work applied" color={COLORS.submitted} accent width={140} />
      </div>
      <div style={{ width: 16 }} />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <ArrowDown height={24} color={COLORS.superseded} />
        <Node label="SUPERSEDED" sub="Flushed by regen" color={COLORS.superseded} width={140} dashed />
      </div>
    </div>

    {/* Legend */}
    <div
      style={{
        marginTop: 24,
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}
    >
      <Badge label="Lease: 15 min" color={COLORS.claimed} />
      <Badge label="No re-enqueue" color="var(--text-secondary)" />
      <Badge label="Release → back to QUEUED" color={COLORS.queued} />
      <Badge label="Expired lease → SUPERSEDED at next regen" color={COLORS.superseded} />
    </div>
  </div>
);

// ─── 2. Queue Phase Progression ──────────────────────────────────────────────

const PhaseNode: React.FC<{
  phase: number;
  label: string;
  sub: string;
  color: string;
  blocks: string;
}> = ({ phase, label, sub, color, blocks }) => (
  <div
    style={{
      ...nodeBase,
      borderLeft: `3px solid ${color}`,
      width: '100%',
      textAlign: 'left',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    }}
  >
    <span
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 18,
        fontWeight: 700,
        color,
        opacity: 0.6,
        minWidth: 24,
      }}
    >
      {phase}
    </span>
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 600, fontSize: 13, color }}>{label}</div>
      <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 1 }}>{sub}</div>
    </div>
    <div
      style={{
        fontSize: 9,
        color: 'var(--text-tertiary)',
        fontFamily: 'var(--font-mono)',
        textAlign: 'right',
        maxWidth: 180,
      }}
    >
      blocks: {blocks}
    </div>
  </div>
);

const QueuePhaseDiagram: React.FC = () => (
  <div style={{ display: 'flex', gap: 32 }}>
    {/* Main pipeline */}
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0 }}>
      <PhaseNode
        phase={0}
        label="FD_CLASSIFICATION"
        sub="Review false detection pipeline flags — confirm, dismiss, or reassign (event-driven, not regen)"
        color={COLORS.findFirst}
        blocks="detection batch"
      />
      <ArrowDown height={20} color={COLORS.findFirst} />
      <PhaseNode
        phase={1}
        label="UNCERTAIN_CLASSIFICATION"
        sub="Single-crop tasks — classify uncertain crops (unassigned crops handled via DESIGN_REVIEW highlights)"
        color={COLORS.assign}
        blocks="all designs under this UPC"
      />
      <ArrowDown height={20} color={COLORS.assign} />
      <PhaseNode
        phase={2}
        label="DESIGN_CURATOR"
        sub="Unified design curation — enrich, cleanup, merge, split in one UI"
        color={COLORS.curator}
        blocks="this design"
      />
      <ArrowDown height={20} color={COLORS.designReview} />
      <PhaseNode
        phase={3}
        label="DESIGN_REVIEW"
        sub="Cross-design cleanup for related UPC clusters — merge designs, move misplaced crops, mark carton/non-front"
        color={COLORS.designReview}
        blocks="all designs under this UPC"
      />

      {/* Legend */}
      <div
        style={{
          marginTop: 20,
          padding: '10px 14px',
          background: 'var(--elevated)',
          borderRadius: 6,
          fontSize: 11,
          color: 'var(--text-secondary)',
          lineHeight: 1.6,
        }}
      >
        Each phase only generates tasks for entities <strong>not blocked</strong> by earlier phases.
        A design blocked by Phase 1 (ASSIGN) cannot get a Phase 2 (CURATOR) task until
        the ASSIGN task is submitted and the next regeneration runs.
      </div>
    </div>

    {/* Side channel: QA Review Layer */}
    <div
      style={{
        width: 180,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 8,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-tertiary)',
          marginBottom: 8,
        }}
      >
        QA Layer
      </div>
      <Node
        label="QA_REVIEW"
        sub="Second-pass sampling on submitted tasks. Admin-only. Configurable rate per queue."
        color={COLORS.qa}
        accent
        width={180}
      />
      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Badge label="Not a task type" color={COLORS.qa} />
        <Badge label="Invisible to labelers" color="var(--text-secondary)" />
        <Badge label="qa_sample_rate config" color="var(--text-secondary)" />
      </div>
    </div>
  </div>
);

// ─── 3. Regeneration Cycle ───────────────────────────────────────────────────

const RegenStep: React.FC<{
  step: number;
  label: string;
  detail: string;
  color?: string;
}> = ({ step, label, detail, color = 'var(--accent)' }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
    }}
  >
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: `${color}18`,
        border: `2px solid ${color}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--font-mono)',
        fontSize: 12,
        fontWeight: 700,
        color,
        flexShrink: 0,
      }}
    >
      {step}
    </div>
    <div style={{ paddingTop: 3 }}>
      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{label}</div>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.5 }}>
        {detail}
      </div>
    </div>
  </div>
);

const RegenCycleDiagram: React.FC = () => (
  <div style={{ display: 'flex', gap: 32 }}>
    {/* Steps */}
    <div style={{ flex: 1 }}>
      {/* Trigger */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginBottom: 20,
          padding: '12px 14px',
          background: 'var(--elevated)',
          borderRadius: 6,
          border: '1px solid var(--border)',
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-tertiary)', marginBottom: 6 }}>
            Trigger Conditions
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Badge label="queued_count == 0" color="var(--accent)" />
            <Badge label="admin on-demand" color="var(--blue)" />
            <Badge label="nightly cron" color={COLORS.cron} />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <RegenStep
          step={1}
          label="Acquire distributed lock"
          detail="Redis SETNX forge:regen_lock:{chain_id} with TTL (default 300s). If held, skip."
          color={COLORS.redis}
        />
        <RegenStep
          step={2}
          label="Snapshot in-flux entities"
          detail="Query claimed tasks with valid leases. Build exclusion sets of design _ids and crop_ids."
          color={COLORS.mysql}
        />
        <RegenStep
          step={3}
          label="Flush stale queued tasks"
          detail="Set all queued tasks to SUPERSEDED. Delete Redis list entries for this chain."
          color="var(--warning)"
        />
        <RegenStep
          step={4}
          label="Mark expired leases as superseded"
          detail="Claimed tasks with lease_expires_at < NOW() are superseded. They'll be recreated if still needed."
          color="var(--warning)"
        />
        <RegenStep
          step={5}
          label="Generate non-overlapping tasks"
          detail="Run the task generation algorithm. Pre-compute candidates via Ray for UNCERTAIN_CLASSIFICATION, DESIGN_CURATOR, and DESIGN_REVIEW. FD_CLASSIFICATION is event-driven (not generated here)."
          color="var(--accent)"
        />
        <RegenStep
          step={6}
          label="Push to Redis queues"
          detail="RPUSH task_ids to forge:queue:{queue_name}:{priority_class} lists. High → Normal → Low."
          color={COLORS.redis}
        />
        <RegenStep
          step={7}
          label="Log and release lock"
          detail="Write to forge_regen_log (tasks created, duration). DEL the lock key."
          color={COLORS.mysql}
        />
      </div>
    </div>

    {/* Infrastructure sidebar */}
    <div style={{ width: 200, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-tertiary)',
          marginBottom: 2,
        }}
      >
        Infrastructure
      </div>
      <Node label="Redis" sub="Queue lists + distributed locks" color={COLORS.redis} accent width={200} />
      <Node label="MySQL" sub="Task state, design metadata, regen logs" color={COLORS.mysql} accent width={200} />
      <Node label="MongoDB" sub="Design _id docs + crop/embedding design_id refs" color={COLORS.mongo} accent width={200} />
      <Node label="Ray" sub="Embedding search for candidate pre-computation" color={COLORS.ray} accent width={200} />
      <Node label="Cron" sub="Monitor loop every 60s + nightly safety net" color={COLORS.cron} accent width={200} />
    </div>
  </div>
);

// ─── 4. Labeler Task Flow ────────────────────────────────────────────────────

const LabelerFlowDiagram: React.FC = () => (
  <div>
    {/* Main horizontal flow */}
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        overflowX: 'auto',
        paddingBottom: 8,
      }}
    >
      <Node label="Home Page" sub="Stats + queue health" color="var(--accent)" width={120} />
      <ArrowRight label="click Start" width={80} />
      <Node label="Queue Select" sub="Pick from assigned queues" color="var(--blue)" width={120} />
      <ArrowRight label="select queue" width={80} />
      <Node label="Task Player" sub="Mounted, idle state" color="var(--purple)" width={120} />
      <ArrowRight label="POST /next" width={80} />
      <Node label="Task Active" sub="Render type-specific UI" color="var(--accent)" accent width={130} />
      <ArrowRight label="submit/skip" width={80} />
      <Node label="Prefetch" sub="Fire /next immediately" color="var(--blue)" width={120} />
    </div>

    {/* Loop arrow indicator */}
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        margin: '12px 0',
        gap: 8,
        alignItems: 'center',
      }}
    >
      <div style={{ height: 1, flex: 1, maxWidth: 200, background: 'var(--border)' }} />
      <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
        instant swap — loop continues until navigate away or queue empty
      </span>
      <div style={{ height: 1, flex: 1, maxWidth: 200, background: 'var(--border)' }} />
    </div>

    {/* Exit paths */}
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 12,
        marginTop: 16,
      }}
    >
      <div style={{ ...nodeBase, borderTop: '2px solid var(--warning)', textAlign: 'center' }}>
        <div style={{ fontWeight: 600, fontSize: 11, color: 'var(--warning)' }}>Lease Expires</div>
        <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 3 }}>
          Visible countdown in header. Amber at &lt;2m, red at &lt;30s. Navigate to home.
        </div>
      </div>
      <div style={{ ...nodeBase, borderTop: '2px solid var(--text-tertiary)', textAlign: 'center' }}>
        <div style={{ fontWeight: 600, fontSize: 11, color: 'var(--text-secondary)' }}>Navigate Away</div>
        <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 3 }}>
          Sidebar nav click. Release via beforeunload / visibilitychange (sendBeacon).
        </div>
      </div>
      <div style={{ ...nodeBase, borderTop: '2px solid var(--danger)', textAlign: 'center' }}>
        <div style={{ fontWeight: 600, fontSize: 11, color: 'var(--danger)' }}>Submit Error</div>
        <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 3 }}>
          Precondition failed (rare). Show error message. Task marked REJECTED.
        </div>
      </div>
      <div style={{ ...nodeBase, borderTop: '2px solid var(--blue)', textAlign: 'center' }}>
        <div style={{ fontWeight: 600, fontSize: 11, color: 'var(--blue)' }}>Queue Empty</div>
        <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 3 }}>
          /next returns empty. Navigate home with "no more tasks" message.
        </div>
      </div>
    </div>

    {/* Keyboard shortcuts */}
    <div
      style={{
        marginTop: 20,
        padding: '12px 14px',
        background: 'var(--elevated)',
        borderRadius: 6,
        display: 'flex',
        gap: 16,
        flexWrap: 'wrap',
        alignItems: 'center',
      }}
    >
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-tertiary)',
        }}
      >
        Keyboard (panel-scoped)
      </span>
      {[
        ['1–9', 'Select candidate'],
        ['Enter', 'Submit'],
        ['S', 'Skip'],
        ['R', 'Reject'],
        ['K', 'Keep (split)'],
      ].map(([key, action]) => (
        <span key={key} style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          <kbd
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 3,
              padding: '1px 5px',
              marginRight: 4,
            }}
          >
            {key}
          </kbd>
          {action}
        </span>
      ))}
    </div>
  </div>
);

// ─── 5. Architecture Map ─────────────────────────────────────────────────────

interface FileEntry {
  name: string;
  type: 'dir' | 'file' | 'new-file' | 'new-dir';
  note?: string;
  children?: FileEntry[];
  color?: string;
}

const TreeNode: React.FC<{ entry: FileEntry; depth: number }> = ({ entry, depth }) => {
  const isDir = entry.type === 'dir' || entry.type === 'new-dir';
  const isNew = entry.type === 'new-file' || entry.type === 'new-dir';
  const indent = depth * 20;

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          paddingLeft: indent,
          paddingTop: 3,
          paddingBottom: 3,
          fontSize: 12,
          lineHeight: 1.4,
        }}
      >
        <span style={{ color: isDir ? 'var(--warning)' : entry.color ?? 'var(--text-secondary)', fontSize: 11 }}>
          {isDir ? '/' : ' '}
        </span>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: isNew ? 'var(--accent)' : 'var(--text-primary)',
            fontWeight: isDir ? 600 : 400,
          }}
        >
          {entry.name}
        </span>
        {entry.note && (
          <span style={{ fontSize: 10, color: 'var(--text-tertiary)', marginLeft: 4 }}>
            — {entry.note}
          </span>
        )}
        {isNew && <Badge label="NEW" color="var(--accent)" />}
      </div>
      {entry.children?.map((child, i) => <TreeNode key={i} entry={child} depth={depth + 1} />)}
    </>
  );
};

const frontendTree: FileEntry[] = [
  {
    name: 'src/forge/',
    type: 'new-dir',
    note: 'All Forge frontend code',
    children: [
      {
        name: 'components/',
        type: 'dir',
        children: [
          {
            name: 'layout/',
            type: 'dir',
            note: 'ForgeLayout, ForgeHeader, ForgeSidebar',
            children: [
              { name: 'ForgeLayout.tsx', type: 'file', note: 'Sidebar + header + outlet wrapper' },
              { name: 'ForgeHeader.tsx', type: 'file', note: 'Page title, theme toggle, profile' },
              { name: 'ForgeSidebar.tsx', type: 'file', note: 'Nav with collapse, drag-to-resize' },
            ],
          },
          {
            name: 'common/',
            type: 'dir',
            note: 'Shared UI primitives',
            children: [
              { name: 'StatCard.tsx', type: 'file', note: 'Animated metric card' },
              { name: 'SimilarityBar.tsx', type: 'file', note: 'Color-coded similarity display' },
              { name: 'Badge.tsx', type: 'file', note: 'Priority pill' },
              { name: 'ComingSoon.tsx', type: 'file', note: 'Placeholder component' },
              { name: 'ForgeToast.tsx', type: 'new-file', note: 'Forge-themed toast notifications' },
            ],
          },
        ],
      },
      {
        name: 'pages/',
        type: 'dir',
        children: [
          { name: 'home/ForgeHomePage.tsx', type: 'file', note: 'Stats, queue health, start working' },
          {
            name: 'task-player/',
            type: 'dir',
            note: '4 task UIs + player shell',
            children: [
              { name: 'TaskPlayerPage.tsx', type: 'file', note: 'Player shell, state machine' },
              { name: 'TaskTypeRegistry.ts', type: 'file', note: 'Plugin map: type → component' },
              { name: 'FDClassificationUI.tsx', type: 'file', note: 'Review false detection pipeline flags' },
              { name: 'DesignCuratorUI.tsx', type: 'file', note: 'Unified design curation — enrich, cleanup, merge, split' },
              { name: 'UncertainClassificationUI.tsx', type: 'file', note: 'Classify uncertain crops — front vs non-front' },
              { name: 'DesignReviewUI.tsx', type: 'file', note: 'Cross-design cleanup for related UPC clusters' },
            ],
          },
          {
            name: 'admin/',
            type: 'dir',
            children: [
              { name: 'QueueManagementPage.tsx', type: 'file', note: 'Backlogs, regen trigger (LIVE)' },
              { name: 'LabelerManagementPage.tsx', type: 'file', note: 'Checkbox matrix (LIVE)' },
              { name: 'MasterLabelPage.tsx', type: 'file', note: 'Master label editor (LIVE)' },
            ],
          },
          { name: 'dashboard/ForgeDashboardPage.tsx', type: 'file', note: 'Tables + stats (MOCK)' },
          { name: 'profile/ForgeProfilePage.tsx', type: 'file', note: 'User info + stats' },
          {
            name: 'system/',
            type: 'dir',
            children: [
              { name: 'GoalsPage.tsx', type: 'file', note: 'Goals and non-goals' },
              { name: 'TaskListPage.tsx', type: 'file', note: 'Implementation task tracker' },
              { name: 'FlowDiagramsPage.tsx', type: 'file', note: 'This page' },
            ],
          },
        ],
      },
      {
        name: 'services/',
        type: 'new-dir',
        note: 'API client',
        children: [{ name: 'ForgeService.ts', type: 'new-file', note: 'Instance-based: new ForgeService(token)' }],
      },
      {
        name: 'store/',
        type: 'new-dir',
        note: 'Zustand stores',
        children: [
          { name: 'useTaskStore.ts', type: 'new-file', note: 'Task player state machine' },
          { name: 'useDataStore.ts', type: 'new-file', note: 'Cached reference data' },
          { name: 'useNotificationStore.ts', type: 'new-file', note: 'Forge-scoped notifications' },
        ],
      },
      { name: 'types/index.ts', type: 'file', note: 'All Forge TypeScript types' },
      {
        name: 'hooks/',
        type: 'dir',
        children: [
          { name: 'useForgeTheme.ts', type: 'file', note: 'Dark/light toggle + localStorage' },
          { name: 'useSidebar.ts', type: 'file', note: 'Width, collapse, drag-resize' },
          { name: 'useStaggeredEntrance.ts', type: 'file', note: 'Animation helper' },
          { name: 'useCountUp.ts', type: 'file', note: 'Animated number counter' },
        ],
      },
      { name: 'styles/forge-theme.css', type: 'file', note: 'Full design system (CSS vars)' },
      { name: 'mocks/index.ts', type: 'file', note: 'Mock data for development' },
      { name: 'routes.tsx', type: 'file', note: 'Route definitions for /forge/*' },
    ],
  },
];

const backendTree: FileEntry[] = [
  {
    name: 'backend-main/',
    type: 'dir',
    note: 'Monorepo',
    children: [
      {
        name: 'fsim/apps/',
        type: 'dir',
        note: 'FastAPI applications',
        children: [
          { name: 'api/', type: 'dir', note: 'Existing main API (Control, Action, etc.)' },
          {
            name: 'api-dl/',
            type: 'new-dir',
            note: 'New DL API app (Forge + future DL endpoints)',
            children: [
              { name: 'api.py', type: 'new-file', note: 'FastAPI app creation + router registration' },
              { name: 'middleware.py', type: 'new-file', note: 'Auth, services (shared pattern)' },
              {
                name: 'routes/v1/forge/',
                type: 'new-dir',
                note: 'Forge API routes',
                children: [
                  { name: 'task_router.py', type: 'new-file', note: '/next, /submit/*, /skip, /release' },
                  { name: 'design_router.py', type: 'new-file', note: '/search, /{_id}' },
                  { name: 'admin_router.py', type: 'new-file', note: '/queues, /labelers, /config' },
                  { name: 'stats_router.py', type: 'new-file', note: '/labeler/{id}, /queues, /dashboard' },
                ],
              },
            ],
          },
        ],
      },
      {
        name: 'fsim/models/forge/',
        type: 'new-dir',
        note: 'SQLAlchemy models',
        children: [
          { name: 'design.py', type: 'new-file', note: 'ForgeDesign (includes representative_crop_id and barcode)' },
          { name: 'task.py', type: 'new-file', note: 'ForgeTask (8 types, 6 statuses)' },
          { name: 'config.py', type: 'new-file', note: 'QueueConfig, ChainConfig, LabelerAssignment' },
        ],
      },
      {
        name: 'fsim/daos/forge/',
        type: 'new-dir',
        note: 'Data access objects',
        children: [
          { name: 'design_dao.py', type: 'new-file', note: 'CRUD, merge, representative management' },
          { name: 'task_dao.py', type: 'new-file', note: 'Claim, submit, skip, release' },
          { name: 'change_log_dao.py', type: 'new-file', note: 'Append-only writer' },
        ],
      },
      {
        name: 'fsim/services/forge/',
        type: 'new-dir',
        note: 'Business logic',
        children: [
          { name: 'design_service.py', type: 'new-file', note: 'Create, merge, split, deactivation' },
          { name: 'task_engine.py', type: 'new-file', note: 'Routing, claim, submit dispatch' },
          { name: 'regen_service.py', type: 'new-file', note: 'Non-overlapping generation' },
          { name: 'centroid_service.py', type: 'new-file', note: 'Ad-hoc mean embedding computation' },
          { name: 'queue_service.py', type: 'new-file', note: 'Redis push/pop/flush utilities' },
          {
            name: 'submit/',
            type: 'new-dir',
            note: 'Per-type submit handlers',
            children: [
              { name: 'fd_classification.py', type: 'new-file' },
              { name: 'design_curator.py', type: 'new-file' },
              { name: 'uncertain_classification.py', type: 'new-file' },
              { name: 'design_review.py', type: 'new-file' },
            ],
          },
          {
            name: 'detectors/',
            type: 'new-dir',
            note: 'Candidate detection algorithms',
            children: [
              { name: 'merge_detector.py', type: 'new-file', note: 'Centroid-based merge pairs' },
            ],
          },
          { name: 'bootstrap.py', type: 'new-file', note: 'Initial design creation (barcode split)' },
        ],
      },
      {
        name: 'fsim/jobs/forge_regen/',
        type: 'new-dir',
        note: 'Cron job for queue regeneration',
      },
      {
        name: 'schema/sql/ddl_changes/2026/',
        type: 'dir',
        note: 'DDL migrations',
        children: [
          { name: 'update_26_XX_XX_forge_change_log.sql', type: 'new-file' },
          { name: 'update_26_XX_XX_forge_tasks.sql', type: 'new-file' },
          { name: 'update_26_XX_XX_forge_config.sql', type: 'new-file' },
          { name: 'update_26_XX_XX_forge_regen.sql', type: 'new-file' },
        ],
      },
    ],
  },
];

const ArchitectureMapDiagram: React.FC = () => {
  const [showBackend, setShowBackend] = useState(true);
  const [showFrontend, setShowFrontend] = useState(true);

  return (
    <div>
      {/* Toggle buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          onClick={() => setShowFrontend((v) => !v)}
          style={{
            padding: '4px 12px',
            borderRadius: 12,
            border: `1px solid ${showFrontend ? 'var(--accent-border)' : 'var(--border)'}`,
            background: showFrontend ? 'var(--accent-subtle)' : 'transparent',
            color: showFrontend ? 'var(--accent)' : 'var(--text-secondary)',
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Frontend (React)
        </button>
        <button
          onClick={() => setShowBackend((v) => !v)}
          style={{
            padding: '4px 12px',
            borderRadius: 12,
            border: `1px solid ${showBackend ? '#8B5CF630' : 'var(--border)'}`,
            background: showBackend ? '#8B5CF610' : 'transparent',
            color: showBackend ? '#8B5CF6' : 'var(--text-secondary)',
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Backend (Python)
        </button>
      </div>

      <div style={{ display: 'flex', gap: 24 }}>
        {showFrontend && (
          <div
            style={{
              flex: 1,
              background: 'var(--elevated)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '14px 16px',
              borderTop: '2px solid var(--accent)',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--accent)',
                marginBottom: 10,
              }}
            >
              Frontend — web-focal-control
            </div>
            {frontendTree.map((entry, i) => (
              <TreeNode key={i} entry={entry} depth={0} />
            ))}
          </div>
        )}

        {showBackend && (
          <div
            style={{
              flex: 1,
              background: 'var(--elevated)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '14px 16px',
              borderTop: '2px solid #8B5CF6',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#8B5CF6',
                marginBottom: 10,
              }}
            >
              Backend — backend-main
            </div>
            {backendTree.map((entry, i) => (
              <TreeNode key={i} entry={entry} depth={0} />
            ))}
          </div>
        )}
      </div>

      {/* Data stores row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 12,
          marginTop: 16,
        }}
      >
        <Node label="MySQL" sub="Design metadata, tasks, configs, change log" color={COLORS.mysql} accent />
        <Node label="MongoDB" sub="Design _id docs + crop/embedding design_id refs" color={COLORS.mongo} accent />
        <Node label="Redis" sub="Queue lists (per queue+priority), regen locks" color={COLORS.redis} accent />
      </div>
    </div>
  );
};

// ─── Page Component ──────────────────────────────────────────────────────────

export const FlowDiagramsPage: React.FC = () => {
  const [expanded, setExpanded] = useState<Set<string>>(
    new Set(['lifecycle', 'phases', 'regen', 'labeler', 'arch'])
  );

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="page-content" style={{ maxWidth: 1020 }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, marginBottom: 6 }}>
          Flow Diagrams
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: 0 }}>
          Visual architecture of the data pipeline, task system, and queue flows.
          Reference these alongside the spec and task list.
        </p>
      </div>

      <Section
        id="lifecycle"
        number={1}
        title="Task Lifecycle"
        icon={<SwapHorizIcon fontSize="small" />}
        specRef="§5.2.3, §6.2"
        description="Every task moves through a simple state machine: created as QUEUED by the regeneration service, CLAIMED when a labeler pops it, then terminally SUBMITTED, SKIPPED, REJECTED (precondition failure), or SUPERSEDED (flushed by next regen). No re-enqueue — abandoned tasks are recreated."
        expanded={expanded.has('lifecycle')}
        onToggle={() => toggle('lifecycle')}
      >
        <TaskLifecycleDiagram />
      </Section>

      <Section
        id="phases"
        number={2}
        title="Queue Phase Progression"
        icon={<AccountTreeIcon fontSize="small" />}
        specRef="§6.5, §6.7"
        description="The regeneration service generates tasks in 7 ordered phases (0-6). Each phase only creates tasks for entities NOT blocked by earlier phases. This guarantees no two active tasks share any entity — eliminating concurrency conflicts by design."
        expanded={expanded.has('phases')}
        onToggle={() => toggle('phases')}
      >
        <QueuePhaseDiagram />
      </Section>

      <Section
        id="regen"
        number={3}
        title="Regeneration Cycle"
        icon={<LoopIcon fontSize="small" />}
        specRef="§6.4"
        description="When a chain's queued task count hits 0, the regeneration service runs. It acquires a distributed lock, snapshots in-flux entities, flushes stale tasks, generates new non-overlapping tasks with pre-computed candidates, pushes them to Redis, and releases the lock. Runs as a cron job (every 60s) plus admin on-demand API."
        expanded={expanded.has('regen')}
        onToggle={() => toggle('regen')}
      >
        <RegenCycleDiagram />
      </Section>

      <Section
        id="labeler"
        number={4}
        title="Labeler Task Flow"
        icon={<PersonIcon fontSize="small" />}
        specRef="§10.4"
        description="The end-to-end labeler experience: land on home page, click Start Working, select a queue, enter the task player. Tasks are fetched server-driven via POST /next. After submit, the next task is prefetched and instantly swapped. Labelers exit by navigating away (task released via beforeunload)."
        expanded={expanded.has('labeler')}
        onToggle={() => toggle('labeler')}
      >
        <LabelerFlowDiagram />
      </Section>

      <Section
        id="arch"
        number={5}
        title="Architecture Map"
        icon={<FolderIcon fontSize="small" />}
        specRef="§9.4, §15.1"
        description="Where components live in the codebase. Frontend is a namespaced module within web-focal-control at src/forge/. Backend is the api-dl app in backend-main (fsim/apps/api-dl/). Files marked NEW need to be created; others already exist."
        expanded={expanded.has('arch')}
        onToggle={() => toggle('arch')}
      >
        <ArchitectureMapDiagram />
      </Section>

      {/* Footer */}
      <div
        style={{
          marginTop: 20,
          fontSize: 11,
          color: 'var(--text-tertiary)',
          textAlign: 'center',
        }}
      >
        Spec v1.0 — See also:{' '}
        <span style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>/forge/system/tasks</span>
        {' '}for implementation task list
      </div>
    </div>
  );
};
